/**
 * Language publishing lifecycle. Values are the backend's, hyphens included: the status
 * rows already store this spelling, and translating it in the response layer would mean
 * maintaining two vocabularies for the same thing.
 *
 * The status is DERIVED server-side from the working copy's assets — no endpoint sets it.
 * `public` is the live state; `archived` is the soft-delete marker, so a deleted language
 * is never also "mid-edit".
 */
export const AICC_LANGUAGE_STATUS = {
  draft: "draft",
  previewReady: "preview-ready",
  publishReady: "publish-ready",
  public: "public",
  archived: "archived",
} as const;

export type AiccLanguageStatus = typeof AICC_LANGUAGE_STATUS[keyof typeof AICC_LANGUAGE_STATUS];

/** The tickets' Assets Status column calls the live state "Published", not "Public". */
export const AICC_LANGUAGE_STATUS_LABEL: Record<AiccLanguageStatus, string> = {
  [AICC_LANGUAGE_STATUS.draft]: "Draft",
  [AICC_LANGUAGE_STATUS.previewReady]: "Preview-Ready",
  [AICC_LANGUAGE_STATUS.publishReady]: "Publish-Ready",
  [AICC_LANGUAGE_STATUS.public]: "Published",
  [AICC_LANGUAGE_STATUS.archived]: "Archived",
};

/**
 * Dot colours for `components/CustomDot`, taken from the palette in utils/color.ts
 * so these statuses read the same way as product and price statuses elsewhere:
 * amber while unfinished, blue while staged, green once live.
 */
export const AICC_LANGUAGE_STATUS_COLOR: Record<AiccLanguageStatus, string> = {
  [AICC_LANGUAGE_STATUS.draft]: "#f8a832",
  [AICC_LANGUAGE_STATUS.previewReady]: "#2f9ce1",
  [AICC_LANGUAGE_STATUS.publishReady]: "#4267F9",
  [AICC_LANGUAGE_STATUS.public]: "#00a854",
  [AICC_LANGUAGE_STATUS.archived]: "#9c9c9c",
};

/** Smaller than CustomDot's 8px default, which reads heavy at table density. */
export const AICC_STATUS_DOT_SIZE = 6;

/**
 * Colour for the header's readiness chip when sub-assets are outstanding. Reuses
 * Draft's amber, since unfinished assets are exactly what holds a language in Draft.
 * A fully ready language gets no colour — the chip should only pull the eye when
 * there is something to act on.
 */
export const AICC_READINESS_BLOCKED_COLOR = AICC_LANGUAGE_STATUS_COLOR[AICC_LANGUAGE_STATUS.draft];

/**
 * The card's left stripe: whether any version of this language is serving end users.
 *
 * Two values, because that is the only thing about a whole language that has a single answer
 * once it can hold two versions at once. Where each version sits in the lifecycle is a property
 * of that version and is stated on its own row — a stripe taking the language's `status` was
 * describing the working copy and saying nothing about the release beside it.
 */
export const AICC_SERVING_COLOR = {
  serving: "#00a854",
  notServing: "#9c9c9c",
};

/**
 * Sub-asset states — its own vocabulary rather than an alias of the language's, because the two
 * spell the live state differently: an asset that is out is `published`, a language that is out
 * is `public`. Sharing one map hid that and would put a value on the wire the reader rejects.
 *
 * Which values appear depends on who is answering. `GET /languages` reports only `draft` or
 * `preview-ready`, since its assets are the working copy's. `GET /languages/{id}/versions`
 * reports all four: the public version's fourteen are `published`, and the working copy's carry
 * whichever step of review they are on.
 */
export const AICC_ASSET_STATUS = {
  draft: "draft",
  previewReady: "preview-ready",
  publishReady: "publish-ready",
  published: "published",
} as const;

export type AiccAssetStatus = typeof AICC_ASSET_STATUS[keyof typeof AICC_ASSET_STATUS];

export const AICC_ASSET_STATUS_LABEL: Record<AiccAssetStatus, string> = {
  [AICC_ASSET_STATUS.draft]: "Draft",
  [AICC_ASSET_STATUS.previewReady]: "Preview-Ready",
  [AICC_ASSET_STATUS.publishReady]: "Publish-Ready",
  [AICC_ASSET_STATUS.published]: "Published",
};

/** The language palette, so one state never reads as two colours across the page. */
export const AICC_ASSET_STATUS_COLOR: Record<AiccAssetStatus, string> = {
  [AICC_ASSET_STATUS.draft]: AICC_LANGUAGE_STATUS_COLOR[AICC_LANGUAGE_STATUS.draft],
  [AICC_ASSET_STATUS.previewReady]: AICC_LANGUAGE_STATUS_COLOR[AICC_LANGUAGE_STATUS.previewReady],
  [AICC_ASSET_STATUS.publishReady]: AICC_LANGUAGE_STATUS_COLOR[AICC_LANGUAGE_STATUS.publishReady],
  [AICC_ASSET_STATUS.published]: AICC_LANGUAGE_STATUS_COLOR[AICC_LANGUAGE_STATUS.public],
};

/**
 * The readable copies of a language's content, which is not the same thing as its release history:
 * `public` is the frozen version end users are on, `working` is the one editable copy, and
 * `withdrawn` is a version an unpublish took down.
 *
 * 🔴 `withdrawn` IS A THIRD SCOPE, not a variant of `public`. Unpublish moves the pointer and copies
 * nothing, so the version's rows stay exactly where they are and test accounts keep reading them —
 * that is what the language is kept around for. It is not public though: `isPublic` is false and
 * standard users get nothing, so labelling it `public` would say the opposite of what unpublish did.
 *
 * It was missing here while the response already carried it, and the omission was silent: a scope
 * nothing matches falls out of every `find`, so the row rendered with no sub-assets at all while the
 * backend was sending fourteen.
 */
export const AICC_VERSION_SCOPE = {
  public: "public",
  withdrawn: "withdrawn",
  working: "working",
} as const;

export type AiccVersionScope = typeof AICC_VERSION_SCOPE[keyof typeof AICC_VERSION_SCOPE];

/**
 * Mostly derived: whichever version `public_version` points at is `public`. The rest are
 * `archived`, except in one case — withdrawing a language that has no working copy hands
 * its published version back as the thing being staged, so that version reads
 * `publish-ready` rather than archived. With a copy in flight there is no slot to hand it
 * to, and it is archived instead.
 */
export const AICC_VERSION_STATUS = {
  public: "public",
  publishReady: "publish-ready",
  archived: "archived",
} as const;

export type AiccVersionStatus = typeof AICC_VERSION_STATUS[keyof typeof AICC_VERSION_STATUS];

export const AICC_VERSION_STATUS_LABEL: Record<AiccVersionStatus, string> = {
  [AICC_VERSION_STATUS.public]: "Public",
  [AICC_VERSION_STATUS.publishReady]: "Publish-Ready",
  [AICC_VERSION_STATUS.archived]: "Archived",
};

/**
 * The acts the audit trail records. Values are the backend's `events[].action`.
 *
 * `published` is the mint — the act the version number stands for — and `republished` is the same content
 * going back out unchanged, which is why they are not one value.
 */
export const AICC_VERSION_EVENT_ACTION = {
  published: "published",
  withdrawn: "withdrawn",
  republished: "republished",
} as const;

export type AiccVersionEventAction = typeof AICC_VERSION_EVENT_ACTION[keyof typeof AICC_VERSION_EVENT_ACTION];

/**
 * Read with the operator after it — "Withdrawn by amy@…". The preposition is part of the label so a
 * future action the backend adds can bring its own phrasing rather than being bent into this one.
 */
export const AICC_VERSION_EVENT_LABEL: Record<AiccVersionEventAction, string> = {
  [AICC_VERSION_EVENT_ACTION.published]: "Published by",
  [AICC_VERSION_EVENT_ACTION.withdrawn]: "Withdrawn by",
  [AICC_VERSION_EVENT_ACTION.republished]: "Republished by",
};

export const AICC_VERSION_STATUS_COLOR: Record<AiccVersionStatus, string> = {
  [AICC_VERSION_STATUS.public]: "#00a854",
  // The same blue Publish-Ready gets elsewhere, so one status is never two colours.
  [AICC_VERSION_STATUS.publishReady]: "#4267F9",
  [AICC_VERSION_STATUS.archived]: "#9c9c9c",
};

/**
 * The sub-asset types from the ticket's language list breakdown. Prompt is split
 * into four separate rows, which is why the backend's existing 10-category
 * setup-status endpoint does not map one-to-one onto this list.
 */
export const AICC_ASSET_TYPE = {
  onboardingPrompt: "onboarding_prompt",
  scenarioConversationPrompt: "scenario_conversation_prompt",
  lessonReviewPrompt: "lesson_review_prompt",
  feedbackAssessmentPrompt: "feedback_assessment_prompt",
  defaultScenarios: "default_scenarios",
  scenarioCategories: "scenario_categories",
  canDoStatement: "can_do_statement",
  lessonInformation: "lesson_information",
  vocabulary: "vocabulary",
  lessonReviewScript: "lesson_review_script",
  sttSetting: "stt_setting",
  ttsSetting: "tts_setting",
  uiElementTranslations: "ui_element_translations",
} as const;

export type AiccAssetType = typeof AICC_ASSET_TYPE[keyof typeof AICC_ASSET_TYPE];

export const AICC_ASSET_TYPE_LABEL: Record<AiccAssetType, string> = {
  [AICC_ASSET_TYPE.onboardingPrompt]: "Prompt: Onboarding",
  [AICC_ASSET_TYPE.scenarioConversationPrompt]: "Prompt: Scenario Conversation",
  [AICC_ASSET_TYPE.lessonReviewPrompt]: "Prompt: Lesson Review",
  [AICC_ASSET_TYPE.feedbackAssessmentPrompt]: "Prompt: Feedback and Assessment",
  [AICC_ASSET_TYPE.defaultScenarios]: "Default Scenarios",
  [AICC_ASSET_TYPE.scenarioCategories]: "Scenario Categories",
  [AICC_ASSET_TYPE.canDoStatement]: "Can-do Statement",
  [AICC_ASSET_TYPE.lessonInformation]: "Lesson Information",
  [AICC_ASSET_TYPE.vocabulary]: "Vocabulary",
  [AICC_ASSET_TYPE.lessonReviewScript]: "Lesson Review Script",
  [AICC_ASSET_TYPE.sttSetting]: "STT Setting",
  [AICC_ASSET_TYPE.ttsSetting]: "TTS Setting",
  [AICC_ASSET_TYPE.uiElementTranslations]: "UI Element Translations",
};

/**
 * Assets that arrive preview-ready and never sit in Draft: they are seeded globally
 * rather than authored per language, so there is nothing for an admin to complete
 * before they count as ready.
 */
export const AICC_PRESEEDED_ASSET_TYPES: AiccAssetType[] = [
  AICC_ASSET_TYPE.defaultScenarios,
  AICC_ASSET_TYPE.scenarioCategories,
  AICC_ASSET_TYPE.uiElementTranslations,
];

/** Display order in the language list's sub-asset breakdown. */
export const AICC_ASSET_TYPES_IN_ORDER: AiccAssetType[] = [
  AICC_ASSET_TYPE.onboardingPrompt,
  AICC_ASSET_TYPE.scenarioConversationPrompt,
  AICC_ASSET_TYPE.lessonReviewPrompt,
  AICC_ASSET_TYPE.feedbackAssessmentPrompt,
  AICC_ASSET_TYPE.defaultScenarios,
  AICC_ASSET_TYPE.scenarioCategories,
  AICC_ASSET_TYPE.canDoStatement,
  AICC_ASSET_TYPE.lessonInformation,
  AICC_ASSET_TYPE.vocabulary,
  AICC_ASSET_TYPE.lessonReviewScript,
  AICC_ASSET_TYPE.sttSetting,
  AICC_ASSET_TYPE.ttsSetting,
  AICC_ASSET_TYPE.uiElementTranslations,
];

/**
 * Who can reach a version. Stated on the row rather than left to a badge: the isolation
 * between the live version and the copy under test is the point of the whole dual state
 * (AICC2-331 §4), so each row says its own answer.
 */
export const AICC_AUDIENCE = {
  allUsers: "All users",
  testAccounts: "Test accounts only",
  nobody: "Not visible yet",
} as const;

/**
 * How the working copy came to exist. Worth stating on its row: a copy cloned from the live version
 * and a copy restored from an archived one look identical afterwards, and which it is decides
 * whether its content is what users have now or something from before.
 */
export const AICC_WORKING_COPY_ORIGIN = {
  duplicatePreview: "duplicate_preview",
  restoreVersion: "restore_version",
  createNewLanguage: "create_new_language",
} as const;

export type AiccWorkingCopyOrigin = typeof AICC_WORKING_COPY_ORIGIN[keyof typeof AICC_WORKING_COPY_ORIGIN];

/**
 * Read with the source version after it — "copied from V5" — except for a new language, which has
 * nothing to have come from.
 */
export const AICC_WORKING_COPY_ORIGIN_LABEL: Record<AiccWorkingCopyOrigin, string> = {
  [AICC_WORKING_COPY_ORIGIN.duplicatePreview]: "copied from",
  [AICC_WORKING_COPY_ORIGIN.restoreVersion]: "restored from",
  [AICC_WORKING_COPY_ORIGIN.createNewLanguage]: "new language",
};

/**
 * What the collapsed card says about each version it has. Read as one line — `V5 live → V6 in
 * preview` — so the words are lowercase and carry the arrow between them.
 */
export const AICC_SUMMARY_STATE = {
  live: "live",
  inPreview: "in preview",
  withdrawn: "withdrawn",
} as const;

/** Shown when a language has neither a published version nor a copy. */
export const AICC_SUMMARY_EMPTY = "Not published";

/**
 * The two kinds of version a language can show at once, plus the one a withdrawal leaves
 * behind. `working` is an editable copy with no version number of its own until it is
 * published; `live` is frozen and serving end users; `withdrawn` is a published version that
 * no longer serves anyone but can go back out unchanged.
 */
export const AICC_VERSION_ROW = {
  live: "live",
  working: "working",
  withdrawn: "withdrawn",
} as const;

export type AiccVersionRowKind = typeof AICC_VERSION_ROW[keyof typeof AICC_VERSION_ROW];

/** Actions the language list offers, gated by state via `languageVersionRows`. */
export const AICC_LANGUAGE_ACTION = {
  readyToPublish: "ready_to_publish",
  publicToAllUser: "public_to_all_user",
  duplicatePreview: "duplicate_preview",
  deleteLanguage: "delete_language",
} as const;

export type AiccLanguageAction = typeof AICC_LANGUAGE_ACTION[keyof typeof AICC_LANGUAGE_ACTION];

export const AICC_LANGUAGE_ACTION_LABEL: Record<AiccLanguageAction, string> = {
  [AICC_LANGUAGE_ACTION.readyToPublish]: "Ready to Publish",
  [AICC_LANGUAGE_ACTION.publicToAllUser]: "Public to All User",
  // The only way to change a public language: assets are read-only while it is live.
  [AICC_LANGUAGE_ACTION.duplicatePreview]: "Duplicate Preview",
  [AICC_LANGUAGE_ACTION.deleteLanguage]: "Delete Language",
};

export const RELEASE_NOTES_MAX_LENGTH = 1000;
